const getGroupedModifiers = (
  originalModifiers = [],
  { hideAllNoOptions = false, quantityNumber = 1 }
) => {
  const modifiers = JSON.parse(JSON.stringify(originalModifiers));
  const subModifierNameLookup = new Map();

  for (const modifier of modifiers) {
    if (
      !modifier.subModifiers ||
      modifier.subModifiers.length === 0 ||
      modifier._id === 'xxx-xxx-xxx'
    )
      continue;

    const subModifierNames = modifier.subModifiers
      .map((sm) => sm.name?.trim()?.toLowerCase())
      .sort()
      .join('|');

    if (!subModifierNameLookup.has(subModifierNames)) {
      subModifierNameLookup.set(subModifierNames, []);
    }
    subModifierNameLookup.get(subModifierNames).push(modifier);
  }

  const groups = {};

  // eslint-disable-next-line no-unused-vars
  for (const [_, modifiersInGroup] of subModifierNameLookup.entries()) {
    if (modifiersInGroup.length > 1) {
      const baseId = modifiersInGroup[0]._id;
      const associatedIds = modifiersInGroup.slice(1).map((m) => m._id);

      const totalModifiersInGroup = associatedIds.length + 1;

      const baseModifier = modifiers.find((m) => m._id === baseId);
      const validSubModifiers = baseModifier.subModifiers.filter(
        (sm) => !sm.name?.toLowerCase()?.split(' ').includes('no')
      );
      const validSubModifierCount = validSubModifiers.length;

      const hasNoOption = baseModifier.subModifiers.some((sm) =>
        sm.name?.toLowerCase()?.split(' ').includes('no')
      );

      let selectionType = 'optional';

      if (!hasNoOption && validSubModifierCount > 0) {
        if (totalModifiersInGroup <= validSubModifierCount) {
          selectionType = 'required_checkbox';
        } else if (totalModifiersInGroup > validSubModifierCount) {
          selectionType = 'required_quantity';
        }
      }

      const optionalMaxSelections = Math.min(
        validSubModifierCount,
        totalModifiersInGroup
      );

      const maxSelections =
        selectionType === 'optional'
          ? optionalMaxSelections
          : totalModifiersInGroup;

      groups[baseId] = {
        selectionType,
        totalModifiersInGroup,
        minSelections: selectionType === 'optional' ? 0 : totalModifiersInGroup,
        maxSelections,
        ids: associatedIds,
      };
    }
  }
  const lookupTable = new Map();

  for (const [baseId, groupInfo] of Object.entries(groups)) {
    lookupTable.set(baseId, {
      isBase: true,
      selectionType: groupInfo.selectionType,
    });
    groupInfo.ids.forEach((id) => {
      lookupTable.set(id, {
        isBase: false,
        selectionType: groupInfo.selectionType,
      });
    });
  }

  const selectedSubModifierNames = new Set();
  const selectedSubModifierQuantities = new Map();

  for (const [baseId, groupInfo] of Object.entries(groups)) {
    let totalSelections = 0;

    const baseModifier = modifiers.find((m) => m._id === baseId);
    if (baseModifier) {
      baseModifier.subModifiers.forEach((subModifier) => {
        if (
          !subModifier.name?.toLowerCase()?.split(' ').includes('no') &&
          subModifier.quantity > 0
        ) {
          totalSelections += subModifier.quantity;
        }
      });
    }

    groupInfo.ids.forEach((associatedId) => {
      const associatedModifier = modifiers.find((m) => m._id === associatedId);
      if (associatedModifier) {
        associatedModifier.subModifiers.forEach((subModifier) => {
          if (
            !subModifier.name?.toLowerCase()?.split(' ').includes('no') &&
            subModifier.quantity > 0
          ) {
            totalSelections += subModifier.quantity;
          }
        });
      }
    });

    groupInfo.totalSelections = totalSelections;
  }

  modifiers.forEach((modifier) => {
    const groupInfo = lookupTable.get(modifier._id);
    if (groupInfo) {
      modifier.subModifiers.forEach((subModifier) => {
        if (
          !subModifier.name?.toLowerCase()?.split(' ').includes('no') &&
          subModifier.quantity > 0
        ) {
          if (groupInfo.selectionType === 'required_quantity') {
            if (
              selectedSubModifierQuantities.get(
                subModifier.name?.trim()?.toLowerCase()
              )
            ) {
              selectedSubModifierQuantities.set(
                subModifier.name?.trim()?.toLowerCase(),
                selectedSubModifierQuantities.get(
                  subModifier.name?.trim()?.toLowerCase()
                ) + subModifier.quantity
              );
            } else {
              selectedSubModifierQuantities.set(
                subModifier.name?.trim()?.toLowerCase(),
                subModifier.quantity
              );
            }
          } else {
            selectedSubModifierNames.add(
              subModifier.name?.trim()?.toLowerCase()
            );
          }
        }
      });
    }
  });

  modifiers.forEach((modifier) => {
    const groupInfo = lookupTable.get(modifier._id);
    if (groupInfo) {
      const baseId = groupInfo.isBase
        ? modifier._id
        : Object.keys(groups).find((key) =>
            groups[key].ids.includes(modifier._id)
          );
      const groupSelectionInfo = groups[baseId];

      if (groupSelectionInfo) {
        const { selectionType, minSelections, maxSelections, totalSelections } =
          groupSelectionInfo;

        modifier.selectionType = selectionType;
        modifier.minSelections = minSelections;
        modifier.maxSelections = maxSelections;
        modifier.totalSelections = totalSelections;

        if (selectionType === 'optional') {
          modifier.isMultiselect = true;
          if (!groupInfo.isBase) {
            modifier.isHiddenMultiselect = true;
          } else if (selectedSubModifierNames.size > 0) {
            modifier.subModifiers.forEach((subModifier) => {
              if (
                selectedSubModifierNames.has(
                  subModifier.name?.trim()?.toLowerCase()
                )
              ) {
                subModifier.isMultiselectSelected = true;
                selectedSubModifierNames.delete(
                  subModifier.name?.trim()?.toLowerCase()
                );
              }
            });
          }
        } else if (selectionType === 'required_checkbox') {
          modifier.isMultiselect = true;
          modifier.isRequiredSelection = true;
          if (!groupInfo.isBase) {
            modifier.isHiddenMultiselect = true;
          } else {
            modifier.subModifiers.forEach((subModifier) => {
              subModifier.isRequiredCheckbox = true;
              if (
                selectedSubModifierNames?.has(
                  subModifier.name?.trim()?.toLowerCase()
                )
              ) {
                subModifier.isMultiselectSelected = true;
                selectedSubModifierNames.delete(
                  subModifier.name?.trim()?.toLowerCase()
                );
              }
            });
          }
        } else if (selectionType === 'required_quantity') {
          modifier.isMultiselect = true;
          modifier.isRequiredSelection = true;
          modifier.isQuantitySelection = true;
          if (!groupInfo.isBase) {
            modifier.isHiddenMultiselect = true;
          } else {
            modifier.subModifiers.forEach((subModifier) => {
              subModifier.isRequiredQuantity = true;
              const trackedQuantity = selectedSubModifierQuantities.get(
                subModifier.name?.trim()?.toLowerCase()
              );
              if (trackedQuantity) {
                subModifier.multiSelectQuantity = trackedQuantity;
                selectedSubModifierQuantities.delete(
                  subModifier.name?.trim()?.toLowerCase()
                );
              } else {
                subModifier.multiSelectQuantity = 0;
              }
            });
          }
        }

        const hasQuantityInThisModifier = modifier.subModifiers.some(
          (subModifier) =>
            !subModifier.name?.toLowerCase()?.split(' ').includes('no') &&
            subModifier.quantity > 0
        );

        modifier.subModifiers.forEach((subModifier) => {
          if (subModifier.name?.toLowerCase()?.split(' ').includes('no')) {
            subModifier.isHiddenMultiselect = true;
            if (!hasQuantityInThisModifier) {
              subModifier.quantity = quantityNumber;
              if ((groupInfo.isBase && hideAllNoOptions) || !groupInfo.isBase) {
                subModifier.isHiddenFromPrinter = true;
              }
            }
          }
        });
      }
    }
  });

  return {
    groupedModifiers: modifiers,
    groupsMultiModifiers: Object.keys(groups).length > 0 ? groups : null,
  };
};

export default getGroupedModifiers;
